import { usePDIContext } from '../context/PDIContext';
import { useSwipe } from '../hooks/useSwipe';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Circle,
  ChevronRight,
  AlertOctagon,
  Camera,
  MessageSquare,
} from 'lucide-react';

const ChecklistItem = ({ item, onExpand, itemIndex }) => {
  const { itemStatuses, updateItemStatus, notes, photos } = usePDIContext();
  
  const status = itemStatuses[item.id] || 'pending';
  const hasNote = notes[item.id] && notes[item.id].trim() !== '';
  const hasPhotos = photos[item.id]?.length > 0;

  // Swipe handlers
  const { handlers, swipeOffset, swipeDirection, swiping } = useSwipe({
    onSwipeRight: () => {
      updateItemStatus(item.id, status === 'pass' ? 'pending' : 'pass');
    },
    onSwipeLeft: () => {
      updateItemStatus(item.id, status === 'fail' ? 'pending' : 'fail');
    },
    threshold: 60,
  });
  
  const statusConfig = {
    pending: {
      icon: Circle,
      color: 'text-slate-300',
      bg: 'bg-white',
      border: 'border-slate-200',
      gradient: '',
    },
    pass: {
      icon: CheckCircle2,
      color: 'text-brand-500',
      bg: 'bg-gradient-to-br from-brand-50 to-emerald-50',
      border: 'border-brand-200',
      gradient: 'from-brand-400 to-emerald-500',
    },
    fail: {
      icon: XCircle,
      color: 'text-danger-500',
      bg: 'bg-gradient-to-br from-danger-50 to-rose-50',
      border: 'border-danger-200',
      gradient: 'from-danger-400 to-rose-500',
    },
    attention: {
      icon: AlertTriangle,
      color: 'text-accent-500',
      bg: 'bg-gradient-to-br from-accent-50 to-amber-50',
      border: 'border-accent-200',
      gradient: 'from-accent-400 to-orange-500',
    },
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  const handleStatusClick = (e, newStatus) => {
    e.stopPropagation();
    if (status === newStatus) {
      updateItemStatus(item.id, 'pending');
    } else {
      updateItemStatus(item.id, newStatus);
    }
  };

  // Calculate swipe styles
  const getSwipeStyles = () => {
    if (!swiping || swipeOffset === 0) return {};
    return {
      transform: `translateX(${swipeOffset}px)`,
      transition: swiping ? 'none' : 'transform 0.3s ease-out',
    };
  };

  return (
    <div 
      className="relative overflow-hidden rounded-2xl animate-fade-in"
      style={{ animationDelay: `${itemIndex * 20}ms` }}
    >
      {/* Swipe Action Indicators (behind the card) */}
      <div className="absolute inset-0 flex">
        {/* Pass indicator (swipe right) */}
        <div 
          className={`flex-1 flex items-center justify-start pl-4 bg-gradient-to-r from-brand-500 to-emerald-500 transition-opacity duration-150 ${
            swipeDirection === 'right' ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex items-center gap-2 text-white font-bold">
            <CheckCircle2 className="w-6 h-6" />
            <span>PASS</span>
          </div>
        </div>
        {/* Fail indicator (swipe left) */}
        <div 
          className={`flex-1 flex items-center justify-end pr-4 bg-gradient-to-l from-danger-500 to-rose-500 transition-opacity duration-150 ${
            swipeDirection === 'left' ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex items-center gap-2 text-white font-bold">
            <span>FAIL</span>
            <XCircle className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Main Card Content */}
      <div 
        {...handlers}
        className={`relative border-2 p-4 transition-all duration-300 checklist-item ${config.bg} ${config.border} ${
          swiping ? '' : 'hover:shadow-md'
        } ${swipeDirection === 'right' ? 'border-brand-400' : swipeDirection === 'left' ? 'border-danger-400' : ''}`}
        style={{
          ...getSwipeStyles(),
          borderRadius: '1rem',
        }}
      >
        <div className="flex items-start gap-3">
          {/* Status Icon */}
          <div className="flex-shrink-0">
            {status !== 'pending' ? (
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-sm`}>
                <StatusIcon className="w-4 h-4 text-white" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                <Circle className="w-4 h-4 text-slate-300" />
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-800 text-sm leading-tight flex items-center gap-1.5 flex-wrap">
                  {item.title}
                  {item.critical && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-danger-100 text-danger-600 rounded text-[10px] font-bold">
                      <AlertOctagon className="w-3 h-3" />
                      CRITICAL
                    </span>
                  )}
                </h4>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                  {item.description}
                </p>
                
                {/* Indicators */}
                {(hasNote || hasPhotos) && (
                  <div className="flex items-center gap-2 mt-2">
                    {hasNote && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/80 rounded-full text-[10px] text-slate-600 font-medium shadow-sm">
                        <MessageSquare className="w-3 h-3 text-brand-500" />
                        Note
                      </span>
                    )}
                    {hasPhotos && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/80 rounded-full text-[10px] text-slate-600 font-medium shadow-sm">
                        <Camera className="w-3 h-3 text-purple-500" />
                        {photos[item.id].length} photo{photos[item.id].length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Expand Button */}
              <button
                onClick={onExpand}
                className="w-8 h-8 rounded-lg bg-white/80 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {/* Quick Status Buttons */}
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={(e) => handleStatusClick(e, 'pass')}
                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all duration-200 btn-touch flex items-center justify-center gap-1.5 ${
                  status === 'pass'
                    ? 'bg-gradient-to-r from-brand-500 to-emerald-500 text-white shadow-lg shadow-brand-500/25'
                    : 'bg-white text-brand-600 border-2 border-brand-200 hover:border-brand-300 hover:bg-brand-50'
                }`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Pass
              </button>
              <button
                onClick={(e) => handleStatusClick(e, 'fail')}
                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all duration-200 btn-touch flex items-center justify-center gap-1.5 ${
                  status === 'fail'
                    ? 'bg-gradient-to-r from-danger-500 to-rose-500 text-white shadow-lg shadow-danger-500/25'
                    : 'bg-white text-danger-600 border-2 border-danger-200 hover:border-danger-300 hover:bg-danger-50'
                }`}
              >
                <XCircle className="w-3.5 h-3.5" />
                Fail
              </button>
              <button
                onClick={(e) => handleStatusClick(e, 'attention')}
                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all duration-200 btn-touch flex items-center justify-center gap-1.5 ${
                  status === 'attention'
                    ? 'bg-gradient-to-r from-accent-500 to-orange-500 text-white shadow-lg shadow-accent-500/25'
                    : 'bg-white text-accent-600 border-2 border-accent-200 hover:border-accent-300 hover:bg-accent-50'
                }`}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Attention</span>
                <span className="sm:hidden">!</span>
              </button>
            </div>

            {/* Swipe hint for pending items (only show on first few) */}
            {status === 'pending' && itemIndex < 2 && (
              <p className="text-[10px] text-slate-400 mt-2 text-center">
                ← Swipe left to fail • Swipe right to pass →
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistItem;
